/**
 * Explore Connections Modal - Unified
 *
 * Supports three rendering modes:
 * - Legacy (Observer): Algorithmic song connections with shuffle
 * - Explorer: Interactive timeline with historical civic moments
 * - Other levels: Block-based content renderer
 */

import { useState, useEffect } from 'react';
import { Compass, Shuffle } from 'lucide-react';
import { StoryCard, IssueTag } from '../lib/types';
import CivicModal from './CivicModal';
import StoryCardItem from './StoryCardItem';
import { getRelatedSongs, getShuffledRelatedSongs, type SongForConnections } from '../lib/exploreConnectionsService';
import { getPublicMediaUrl } from '../lib/mediaHelpers';
import { ActionModalBlock } from '../lib/actionModalBlockTypes';
import { ActionModalBlockRenderer } from './ActionModalBlockRenderer';
import { useExplorer } from '../lib/ExplorerContext';
import { useAuth } from '../lib/AuthContext';
import { fetchTimelineEvents, type TimelineEvent } from '../lib/explorerTimelineService';
import ExplorerTimeline from './ExplorerTimeline';

interface ExploreConnectionsModalUnifiedProps {
  isOpen: boolean;
  onClose: () => void;
  system: 'legacy' | 'new';
  currentSongId: string;
  onSelectSong: (songId: string) => void;
  newBlocks?: ActionModalBlock[];
  civicLevel?: string;
}

export default function ExploreConnectionsModalUnified({
  isOpen,
  onClose,
  system,
  currentSongId,
  onSelectSong,
  newBlocks = [],
  civicLevel,
}: ExploreConnectionsModalUnifiedProps) {
  const { user } = useAuth();
  const { lensFocus, loadLensFocus } = useExplorer();
  const [relatedSongs, setRelatedSongs] = useState<StoryCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [isShuffling, setIsShuffling] = useState(false);
  const [shownSongIds, setShownSongIds] = useState<string[]>([]);

  const isExplorerLevel = civicLevel === 'Explorer';

  // Explorer timeline state
  const [timelineEvents, setTimelineEvents] = useState<TimelineEvent[]>([]);

  // Load lens focus for Explorer level
  useEffect(() => {
    if (isOpen && isExplorerLevel && currentSongId && user) {
      loadLensFocus(currentSongId);
    }
  }, [isOpen, isExplorerLevel, currentSongId, user, loadLensFocus]);

  // Load timeline events for Explorer level
  useEffect(() => {
    if (isOpen && isExplorerLevel && currentSongId && system === 'new') {
      loadTimelineEvents();
    }
  }, [isOpen, isExplorerLevel, currentSongId, system]);

  async function loadTimelineEvents() {
    setLoading(true);
    try {
      const events = await fetchTimelineEvents(currentSongId);
      setTimelineEvents(events);
    } catch (err) {
      console.error('[ExploreConnectionsModalUnified] Error loading timeline:', err);
      setTimelineEvents([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (isOpen && currentSongId && system === 'legacy') {
      setShownSongIds([]);
      loadRelatedSongs();
    }
  }, [isOpen, currentSongId, system]);

  async function loadRelatedSongs(isShuffle = false) {
    if (isShuffle) {
      setIsShuffling(true);
    } else {
      setLoading(true);
    }

    try {
      const connections = isShuffle
        ? await getShuffledRelatedSongs({ id: currentSongId } as SongForConnections, shownSongIds)
        : await getRelatedSongs({ id: currentSongId } as SongForConnections);

      const relatedCards: StoryCard[] = connections.map(song => {
        const primaryTags = song.primary_tags || song.tags || [];
        const secondaryTags = song.secondary_tags || [];

        return {
          id: song.id,
          songTitle: song.title,
          artistName: song.artist,
          albumArtUrl: getPublicMediaUrl(song.image_url),
          tags: primaryTags as IssueTag[],
          broadCategory: (primaryTags[0] || 'Justice') as IssueTag,
          primaryIssueTag: (primaryTags[0] || 'Justice') as IssueTag,
          secondaryIssueTags: (primaryTags.slice(1) || []) as IssueTag[],
          subTags: secondaryTags,
          issueTags: [...primaryTags, ...secondaryTags],
          hookText: song.explore_caption || song.caption || '',
          storyContent: '',
          order: 0,
        };
      });

      // Reorder songs to prioritize lens-aligned songs for Explorer level
      let finalCards = relatedCards;
      if (isExplorerLevel && lensFocus) {
        const lensCategory = lensFocus.lens_category;
        const aligned = relatedCards.filter(card =>
          card.tags.some(tag => tag.toLowerCase() === lensCategory.toLowerCase())
        );
        const others = relatedCards.filter(card =>
          !card.tags.some(tag => tag.toLowerCase() === lensCategory.toLowerCase())
        );
        finalCards = [...aligned, ...others];
      }

      setRelatedSongs(finalCards);

      const newSongIds = connections.map(song => song.id);
      setShownSongIds(prev => [...prev, ...newSongIds]);
    } catch (err) {
      console.error('[ExploreConnectionsModalUnified] Error loading related songs:', err);
      setRelatedSongs([]);
    } finally {
      if (isShuffle) {
        setIsShuffling(false);
      } else {
        setLoading(false);
      }
    }
  }

  function handleShuffle() {
    loadRelatedSongs(true);
  }

  function handleSelectSong(songId: string) {
    onSelectSong(songId);
    onClose();
  }

  const getCategoryDisplayName = (category: string): string => {
    const map: Record<string, string> = {
      power: 'Power',
      justice: 'Justice',
      systems: 'Systems',
      identity: 'Identity',
      resistance: 'Resistance',
      institutions: 'Institutions',
      visibility: 'Visibility',
      collective_action: 'Collective Action',
      historical_patterns: 'Historical Patterns',
      structural_change: 'Structural Change',
    };
    return map[category] || category;
  };

  const renderLegacyContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div
            className="w-8 h-8 border-4 rounded-full animate-spin"
            style={{
              borderColor: '#40E0D0',
              borderTopColor: 'transparent',
            }}
          ></div>
        </div>
      );
    }

    if (relatedSongs.length === 0) {
      return (
        <div className="text-center py-12">
          <p className="text-[14px] text-[#A0A0A0]" style={{ lineHeight: '1.55' }}>
            No related songs available yet.
          </p>
        </div>
      );
    }

    return (
      <div>
        {/* Lens Callout Banner - Explorer Level Only */}
        {isExplorerLevel && lensFocus && (
          <div
            className="mb-4 p-3 rounded-lg border border-[#40E0D0]/30 bg-[#40E0D0]/5"
          >
            <div className="flex items-start gap-2">
              <div className="mt-0.5">
                <Compass className="w-4 h-4 text-[#40E0D0]" />
              </div>
              <div>
                <div className="text-[12px] font-semibold text-[#40E0D0] uppercase tracking-wide mb-1">
                  Lens: {getCategoryDisplayName(lensFocus.lens_category)}
                </div>
                <p className="text-[11px] text-[#B0B0B0] leading-relaxed">
                  Songs sharing the {getCategoryDisplayName(lensFocus.lens_category).toLowerCase()} lens appear first
                </p>
              </div>
            </div>
          </div>
        )}

        <div
          className="transition-opacity duration-200"
          style={{ opacity: isShuffling ? 0.4 : 1 }}
        >
          <div className="space-y-3">
            {relatedSongs.map(song => (
              <StoryCardItem
                key={song.id}
                card={song}
                onClick={() => handleSelectSong(song.id)}
                variant="modal"
              />
            ))}
          </div>

          <div className="flex justify-center mt-6">
            <button
              onClick={handleShuffle}
              disabled={isShuffling}
              className="flex items-center gap-2 px-8 py-2 rounded-lg bg-[#1A1A1A] border border-[#40E0D0] text-[#40E0D0] hover:bg-[#40E0D0]/10 transition-colors text-[13px] font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Shuffle className={`w-4 h-4 ${isShuffling ? 'animate-spin' : ''}`} />
              Shuffle
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderExplorerTimeline = () => {
    return (
      <div className="w-full">
        {/* Helper text */}
        <p
          className="text-[13px] text-[#A0A0A0] mb-4"
          style={{ lineHeight: '1.5' }}
        >
          Tap a moment to see how this story echoes across time.
        </p>

        {/* Timeline card - aligned with modal header */}
        <div
          className="w-full rounded-xl border shadow-lg"
          style={{
            backgroundColor: '#1A1A1A',
            borderColor: 'rgba(255, 255, 255, 0.12)',
            maxHeight: '480px',
            overflowY: 'auto',
          }}
        >
          <div className="p-4 md:p-5">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div
                  className="w-8 h-8 border-4 rounded-full animate-spin"
                  style={{
                    borderColor: '#40E0D0',
                    borderTopColor: 'transparent',
                  }}
                ></div>
              </div>
            ) : (
              <ExplorerTimeline events={timelineEvents} />
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderNewBlocks = () => {
    if (newBlocks.length === 0) {
      return (
        <div
          className="bg-[#1A1A1A] rounded-xl border"
          style={{
            borderColor: 'rgba(255, 255, 255, 0.08)',
            padding: '32px 24px',
          }}
        >
          <p className="text-[14px] text-[#E0E0E0]" style={{ lineHeight: '1.6' }}>
            No content available for this level yet.
          </p>
        </div>
      );
    }

    return (
      <div
        className="bg-[#1A1A1A] rounded-xl border"
        style={{
          borderColor: 'rgba(255, 255, 255, 0.08)',
          padding: '32px 24px',
        }}
      >
        {newBlocks.map((block, index) => (
          <div key={block.id || index}>
            <ActionModalBlockRenderer block={block} />
          </div>
        ))}
      </div>
    );
  };

  // Determine which content to render
  const renderContent = () => {
    if (system === 'legacy') {
      return renderLegacyContent();
    }

    if (isExplorerLevel) {
      return renderExplorerTimeline();
    }

    return renderNewBlocks();
  };

  return (
    <CivicModal
      isOpen={isOpen}
      onClose={onClose}
      title="Explore Connections"
      subtitle={
        system === 'legacy'
          ? 'Songs connected by theme'
          : isExplorerLevel
          ? 'Historical moments and civic roots'
          : undefined
      }
      icon={Compass}
      maxWidth="lg"
    >
      {renderContent()}
    </CivicModal>
  );
}
